import React, { useState, useEffect } from 'react';
import { Form, FormField, FormLabel, FormInput, FormSelect, FormCheckbox, Button } from '../ui';
import type { SurveyQuestion, QuestionFormData } from '../../types/survey';

const questionTypes = [
  { value: 'boolean', label: 'Sim/Não' },
  { value: 'text', label: 'Texto livre' },
  { value: 'single_choice', label: 'Escolha única' },
  { value: 'multiple_choice', label: 'Múltipla escolha' },
];

interface QuestionFormProps {
  question?: SurveyQuestion | null;
  onSubmit: (data: QuestionFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const QuestionForm: React.FC<QuestionFormProps> = ({
  question,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const [questionText, setQuestionText] = useState('');
  const [questionType, setQuestionType] = useState<QuestionFormData['question_type']>('text');
  const [orderIndex, setOrderIndex] = useState(0);
  const [isRequired, setIsRequired] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (question) {
      setQuestionText(question.question_text);
      setQuestionType(question.question_type);
      setOrderIndex(question.order_index);
      setIsRequired(question.is_required);
    } else {
      setQuestionText('');
      setQuestionType('text');
      setOrderIndex(0);
      setIsRequired(true);
    }
    setErrors({});
  }, [question]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!questionText.trim()) errs.question_text = 'Texto da pergunta é obrigatório';
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    onSubmit({
      question_text: questionText.trim(),
      question_type: questionType,
      order_index: orderIndex,
      is_required: isRequired,
    });
  };

  return (
    <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField>
        <FormLabel htmlFor="question_text" required>Texto da pergunta</FormLabel>
        <FormInput
          id="question_text"
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="Digite a pergunta..."
          error={!!errors.question_text}
          validationError={errors.question_text}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="question_type" required>Tipo</FormLabel>
          <FormSelect
            id="question_type"
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value as QuestionFormData['question_type'])}
          >
            {questionTypes.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="order_index">Ordem</FormLabel>
          <FormInput
            id="order_index"
            type="number"
            min={0}
            value={orderIndex}
            onChange={(e) => setOrderIndex(Number(e.target.value))}
          />
        </FormField>
      </div>

      <FormField>
        <label className="flex items-center gap-2 cursor-pointer">
          <FormCheckbox
            id="is_required"
            checked={isRequired}
            onChange={(e) => setIsRequired(e.target.checked)}
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Resposta obrigatória</span>
        </label>
      </FormField>

      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button type="submit" isLoading={isLoading}>
          {question ? 'Salvar' : 'Criar'}
        </Button>
      </div>
    </Form>
  );
};
